import { observer } from "mobx-react";
import React from "react";
import { useParams } from "react-router-dom";
import { useBreakpoint, useWebGalleryStore } from "../../hooks";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { Icon, IconGlyph } from "../../lib/Icon";
import Tooltip from "../../lib/Tooltip";
import {
  formatBytes,
  generateTooltipContent,
  isAssetSharedAsOriginal,
} from "../../util";
import { DownloadButton } from "./DownloadButton";
import { LanguageDropdown } from "./LanguageDropdown";
import { ViewPortToggle } from "./ViewPortToggle";
import { ZoomControl } from "./ZoomControl";

export interface GalleryHeaderProps {
  zoomLevel?: number | undefined;
  incrementZoom?: (event: React.MouseEvent) => void;
  decrementZoom?: (event: React.MouseEvent) => void;
  getNextAsset?: (event: React.MouseEvent) => void;
  getPreviousAsset?: (event: React.MouseEvent) => void;
  downloadUrl?: string;
  hasPreviousAsset?: boolean;
  hasNextAsset?: boolean;
  canZoomIn?: boolean;
  canZoomOut?: boolean;
  logoSrc?: string;
  multilanguageEnabled?: boolean;
  sharedRenditionFileExtension?: string | undefined;
  sharedRenditionWidth?: number | undefined;
  sharedRenditionHeight?: number | undefined;
  sharedRenditionFileSizeInBytes?: number | undefined;
}

export interface IWebGalleryUrlParams {
  galleryId: string;
  assetId: string;
}

export const GalleryHeader = observer(() => {
  const store = useWebGalleryStore();
  const { assetId } = useParams<IWebGalleryUrlParams>();

  const isMultiLanguageEnabled = () => {
    return (
      store.features?.UIEnhancements === "Active" &&
      store.features?.MultiLanguage === "Active"
    );
  };

  if (store?.assets?.length === 0) {
    return <GalleryDefaultHeader logoSrc={store.logoSrc} />;
  }
  if (
    // on the WebGallery page
    (store.gallery && !assetId) ||
    // toggled to detail view with pending or rejected assets
    (store.gallery && assetId === "undefined")
  ) {
    return (
      <GalleryGridHeader
        incrementZoom={store.zoomIn}
        decrementZoom={store.zoomOut}
        zoomLevel={store.zoomLevel}
        canZoomIn={store.canZoomIn}
        canZoomOut={store.canZoomOut}
        hasPreviousAsset={store.hasPreviousAsset}
        hasNextAsset={store.hasNextAsset}
        logoSrc={store.logoSrc}
        multilanguageEnabled={isMultiLanguageEnabled()}
      />
    );
  }
  // on WebDetail page with valid selected asset
  if (assetId && store.selectedAsset) {
    return (
      <GalleryDetailHeader
        downloadUrl={store.selectedAsset.media.download}
        getNextAsset={store.getNextAsset}
        getPreviousAsset={store.getPreviousAsset}
        hasPreviousAsset={store.hasPreviousAsset}
        hasNextAsset={store.hasNextAsset}
        logoSrc={store.logoSrc}
        multilanguageEnabled={isMultiLanguageEnabled()}
        sharedRenditionFileExtension={
          store.selectedAsset.file.sharedRenditionFileExtension
        }
        sharedRenditionWidth={store.selectedAsset.file.sharedRenditionWidth}
        sharedRenditionHeight={store.selectedAsset.file.sharedRenditionHeight}
        sharedRenditionFileSizeInBytes={
          store.selectedAsset.file.sharedRenditionFileSizeInBytes
        }
      />
    );
  }
  return <GalleryDefaultHeader logoSrc={store.logoSrc} />;
});

export const GalleryDetailHeader = ({
  getNextAsset,
  getPreviousAsset,
  hasPreviousAsset,
  hasNextAsset,
  downloadUrl,
  logoSrc = "https://styles.mediavalet.net/logos/mvlogo.png",
  multilanguageEnabled,
  sharedRenditionFileExtension,
  sharedRenditionWidth,
  sharedRenditionHeight,
  sharedRenditionFileSizeInBytes,
}: GalleryHeaderProps): JSX.Element => {
  let { t } = useCustomTranslation("Header");
  let { isDesktop } = useBreakpoint();

  const renderTooltipContent = () => {
    const sharedRenditionFileSizeFormatted = formatBytes(
      sharedRenditionFileSizeInBytes
    );

    const tooltipContentParts = generateTooltipContent(
      sharedRenditionFileExtension,
      sharedRenditionWidth,
      sharedRenditionHeight,
      sharedRenditionFileSizeFormatted
    );

    return (
      <>
        <div>{t("RenditionAvailable")}</div>
        <div>{tooltipContentParts.join(" • ")}</div>
      </>
    );
  };

  return (
    <div className="gallery-header">
      <div className="gallery-header__logo">
        <img alt="MediaValet" className="logo" src={logoSrc} />
        {multilanguageEnabled && (
          <div className="language-toggle__wrapper">
            <LanguageDropdown />
          </div>
        )}
      </div>
      <div className="gallery-header__nav">
        <div className="gallery-header__nav-left">
          <ViewPortToggle />
        </div>
        <div className="gallery-header__nav-right">
          {downloadUrl && isDesktop && (
            <div className="download-button__wrap">
              <DownloadButton
                href={downloadUrl}
                label={`${t("Download")} ${
                  sharedRenditionFileExtension
                    ? sharedRenditionFileExtension.toUpperCase()
                    : ""
                }`}
              />
              {/* this is to make sure there's no tooltip for older web galleries which don't have this info */}
              {sharedRenditionFileExtension &&
                !isAssetSharedAsOriginal(downloadUrl) && (
                  <Tooltip
                    wrapperClassName={"assets-browser__top-bar-tooltip"}
                    tooltipContent={renderTooltipContent()}
                    position="down"
                    trigger="mouseover"
                  >
                    <Icon glyph={IconGlyph.QUESTION} />
                  </Tooltip>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const GalleryGridHeader = ({
  zoomLevel,
  incrementZoom,
  decrementZoom,
  canZoomIn,
  canZoomOut,
  logoSrc = "https://styles.mediavalet.net/logos/mvlogo.png",
  multilanguageEnabled,
}: GalleryHeaderProps): JSX.Element => {
  let { isDesktop } = useBreakpoint();

  return (
    <div className="gallery-header">
      <div className="gallery-header__logo">
        <img alt="MediaValet" className="logo" src={logoSrc} />
        {multilanguageEnabled && (
          <div className="language-toggle__wrapper">
            <LanguageDropdown />
          </div>
        )}
      </div>
      <div className="gallery-header__nav">
        <div className="gallery-header__nav-left">
          <ViewPortToggle />
          {isDesktop && (
            <ZoomControl
              zoomLevel={zoomLevel}
              incrementZoom={incrementZoom}
              decrementZoom={decrementZoom}
              canZoomIn={canZoomIn}
              canZoomOut={canZoomOut}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const GalleryDefaultHeader = (
  props: GalleryHeaderProps
): JSX.Element => {
  let { isDesktop } = useBreakpoint();

  if (isDesktop) {
    return (
      <div className="gallery-header">
        <div className="gallery-header__logo">
          <img alt="MediaValet" className="logo" src={props.logoSrc} />
        </div>
      </div>
    );
  }
  return (
    <div className="gallery-header">
      <div className="gallery-header__logo">
        <img alt="MediaValet" className="logo" src={props.logoSrc} />
        <ViewPortToggle />
      </div>
    </div>
  );
};

GalleryDefaultHeader.defaultProps = {
  logoSrc: "https://styles.mediavalet.net/logos/mvlogo.png",
};
